import type { InstatiateEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InstatiateEnterpriseRiskDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

// The generated GraphQL list-scalar variables widen to `string | string[]`;
// the tRPC procedure takes plain arrays, so the hook works with this shape.
export interface InstantiateEnterpriseRiskInput {
  EnterpriseRiskIds: string[];
  Entities: string[];
}

/**
 * Instantiation creates risk trees, so both the risk and enterprise-risk read
 * surfaces are invalidated on success. Note the legacy GraphQL op name is
 * misspelled (`instatiate…`); the hook name is spelled correctly.
 */
export const useInstantiateEnterpriseRisk = createMutationHook({
  name: 'instantiateEnterpriseRisk',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.enterpriseRisk.instantiate.mutationOptions({ onSuccess }),
  mapInput: (variables: InstantiateEnterpriseRiskInput) => ({
    EnterpriseRiskIds: variables.EnterpriseRiskIds,
    Entities: variables.Entities,
  }),
  invalidate: (trpc) => [
    trpc.frontend.risk.register.queryKey(),
    trpc.frontend.risk.riskById.queryKey(),
    // Instantiation creates new descendant risks, so the
    // remove-from-enterprise-risk warning (useGetRiskDescendantIds) must
    // refresh too, matching the register/by-id invalidations.
    trpc.frontend.risk.descendantIds.queryKey(),
    trpc.frontend.enterpriseRisk.register.queryKey(),
    trpc.frontend.enterpriseRisk.getById.queryKey(),
  ],
  // Maps the tRPC `{ affectedRows }` response to the GraphQL mutation shape
  // (V2 reported `affected_rows`).
  mapResponse: (result: {
    affectedRows: number;
  }): InstatiateEnterpriseRiskMutation => ({
    instantiateChildEnterpriseRisk: {
      __typename: 'GenericMutationOutput',
      affected_rows: result.affectedRows,
    },
  }),
  graphqlDocument: InstatiateEnterpriseRiskDocument,
  graphqlVariables: (variables: InstantiateEnterpriseRiskInput) => ({
    ...variables,
  }),
  evictFields: ['risk'],
});
